import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ShoppingCart, CalendarDays } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import CartDrawer from './CartDrawer';
import { MAIN_MENU, ROUTES } from '../data/stjSite';
import { useShopCategories } from '../hooks/useShopCategories';

const isExternal = (href) => /^https?:\/\//i.test(href || '');
const isHashLink = (href) => (href || '').includes('#');

/** Renders the right element type: external <a>, hash <a>, or router <Link>. */
const NavLink = ({ href, external, className, children, ...rest }) => {
  if (external || isExternal(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
        {children}
      </a>
    );
  }
  if (isHashLink(href)) {
    return (
      <a href={href} className={className} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} {...rest}>
      {children}
    </Link>
  );
};

const BrandMark = ({ siteName, logoUrl, overlay, className = 'h-12 md:h-16' }) => (
  logoUrl ? (
    <img
      src={logoUrl}
      alt={siteName || 'Site logo'}
      className={`${className} w-auto max-w-none object-contain`}
    />
  ) : (
    <span className={`text-2xl font-bold whitespace-nowrap ${overlay ? 'text-white' : 'text-slate-900'}`}>
      {siteName}
    </span>
  )
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { cartItems, setIsCartOpen } = useCart();
  const { siteName, logoUrl } = useSiteSettings();
  const flags = useSiteFeatureFlags();
  const { categories: shopCategories } = useShopCategories();
  const { cart_enabled, pawn_checkout, events_enabled, activity_marketplace_enabled } = flags;

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 80);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  /**
   * The home page hero plays a full-bleed video, so there the header floats
   * transparently on top of it with white type. Everywhere else (and once the
   * hero has scrolled away) it becomes a solid light bar with dark type, so the
   * menu is always readable and the light theme is never broken.
   */
  const isHome = location.pathname === '/';
  const overlay = isHome && !isScrolled;

  // EXACT reservestj.com main menu, with flag-gated areas removed.
  const navigation = MAIN_MENU.filter((item) => {
    if (item.label === 'GIFT SHOP') return cart_enabled;
    if (item.label === 'ACTIVITIES') return activity_marketplace_enabled;
    return true;
  })
    // a sub-item can declare a feature flag; hide it when that flag is off
    .map((item) => (item.items
      ? { ...item, items: item.items.filter((sub) => !sub.flag || flags[sub.flag]) }
      : item))
    // GIFT SHOP links to the shop home page and drops down the catalogue's own categories,
    // so a category added in Admin -> Products shows up in the menu with no code change.
    .map((item) => (item.label === 'GIFT SHOP' && shopCategories.length > 0
      ? {
        ...item,
        items: [
          { label: 'Shop Home', href: ROUTES.shop },
          { label: 'All Products', href: '/shop/products' },
          ...shopCategories.map((c) => ({ label: c.name, href: `/shop/category/${c.slug}` })),
        ],
      }
      : item))
    .concat(events_enabled ? [{ label: 'EVENTS', href: '/events' }] : []);

  const handleDropdownToggle = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const topLinkClass = overlay
    ? 'text-white hover:text-white/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]'
    : 'text-slate-700 hover:text-blue-700';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          overlay ? 'bg-transparent' : 'bg-white border-b border-slate-200 shadow-sm'
        }`}
        data-testid="main-header"
        data-overlay={overlay ? 'true' : 'false'}
      >
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between py-3">
            {/* Logo - always from Developer Settings, never restyled */}
            <Link to="/" className="flex items-center shrink-0" data-testid="header-logo">
              <BrandMark siteName={siteName} logoUrl={logoUrl} overlay={overlay} />
            </Link>

            {/* Desktop menu */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navigation.map((item) => (
                <div key={item.label} className="relative group">
                  {item.items ? (
                    <>
                      {item.href ? (
                        <NavLink
                          href={item.href}
                          className={`flex items-center px-3 py-2 text-sm font-bold tracking-wide uppercase transition-colors ${topLinkClass}`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </NavLink>
                      ) : (
                        <button
                          className={`flex items-center px-3 py-2 text-sm font-bold tracking-wide uppercase transition-colors ${topLinkClass}`}
                          onClick={() => handleDropdownToggle(item.label)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </button>
                      )}
                      <div className="absolute top-full left-0 w-72 bg-white border border-slate-200 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 mt-1 overflow-hidden">
                        {item.items.map((subItem) => (
                          <NavLink
                            key={subItem.label}
                            href={subItem.href}
                            external={subItem.external}
                            className="block px-4 py-3 text-slate-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                            data-testid={`nav-item-${subItem.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            {subItem.label}
                          </NavLink>
                        ))}
                      </div>
                    </>
                  ) : (
                    <NavLink
                      href={item.href}
                      className={`px-3 py-2 text-sm font-bold tracking-wide uppercase transition-colors ${topLinkClass}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </NavLink>
                  )}
                </div>
              ))}
            </nav>

            {/* Right side: BOOK DIRECT + cart + hamburger */}
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to={ROUTES.allToursCharters}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-bold rounded hover:bg-red-700 transition-colors shadow-md whitespace-nowrap"
                data-testid="nav-book-direct"
              >
                <CalendarDays className="w-4 h-4" />
                BOOK DIRECT
              </Link>

              {cart_enabled && pawn_checkout && (
                <button
                  onClick={() => setIsCartOpen(true)}
                  className={`relative p-2 transition-colors ${topLinkClass}`}
                  data-testid="cart-button"
                  aria-label="Open cart"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </button>
              )}

              {/* Hamburger is always available, like the live site */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2.5 bg-slate-800/90 text-white rounded hover:bg-slate-900 transition-colors"
                aria-label="Open menu"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Right-hand slide-out menu */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div
          className="absolute inset-0 bg-slate-900/50"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />

        <aside
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl border-l border-slate-200 flex flex-col transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu-panel"
        >
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-menu-logo">
              <BrandMark siteName={siteName} logoUrl={logoUrl} className="h-10" />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-slate-700 hover:text-blue-700"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {navigation.map((item) => (
              <div key={item.label} className="border-b border-slate-100 last:border-b-0">
                {item.items ? (
                  <>
                    <button
                      className="flex items-center justify-between w-full py-3 text-slate-800 font-bold uppercase text-sm tracking-wide"
                      onClick={() => handleDropdownToggle(item.label)}
                    >
                      {item.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          activeDropdown === item.label ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {activeDropdown === item.label && (
                      <div className="pb-3 pl-4 space-y-1">
                        {item.items.map((subItem) => (
                          <NavLink
                            key={subItem.label}
                            href={subItem.href}
                            external={subItem.external}
                            className="block py-2 text-slate-600 hover:text-blue-700 transition-colors"
                          >
                            {subItem.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    href={item.href}
                    className="block py-3 text-slate-800 font-bold uppercase text-sm tracking-wide hover:text-blue-700"
                  >
                    {item.label}
                  </NavLink>
                )}
              </div>
            ))}

            <div className="mt-5">
              <Link
                to={ROUTES.allToursCharters}
                className="flex items-center justify-center gap-2 w-full py-3 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors"
              >
                <CalendarDays className="w-4 h-4" />
                BOOK DIRECT
              </Link>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-200 pt-4 text-sm">
              {cart_enabled && (
                <Link to="/shop" className="text-slate-600 hover:text-blue-700" data-testid="mobile-shop-link">Gift Shop</Link>
              )}
              <Link to="/resources" className="text-slate-600 hover:text-blue-700" data-testid="mobile-resources-link">Resources</Link>
              <Link to="/login" className="text-slate-600 hover:text-blue-700" data-testid="mobile-login-link">Login</Link>
            </div>
          </div>
        </aside>
      </div>

      <CartDrawer />
    </>
  );
};

export default Header;
